/**
 * Адрес, через который открыт кабинет (team-zone-login-page; 152-ФЗ, разделение входов клиентов
 * и сотрудников). Сервер отвечает по заголовку прокси: `team` — внутренний адрес кабинета
 * сотрудников, `client` — внешний. Запрашивается один раз за загрузку страницы; при ошибке —
 * `client` (обычный вход клиента; разделение всё равно обеспечивает сервер).
 */
import { useEffect, useState } from 'react'
import { authApi, type EntryZone } from '@/lib/api'

const CLIENT_ZONE: EntryZone = { zone: 'client' }

let cached: Promise<EntryZone> | null = null
/** Ответ сервера, когда он уже получен, — следующие экраны показывают подписи сразу. */
let resolved: EntryZone | null = null

export function loadEntryZone(): Promise<EntryZone> {
  cached ??= authApi
    .entryZone()
    .catch(() => CLIENT_ZONE)
    .then((z) => {
      resolved = z
      return z
    })
  return cached
}

/** Для тестов: задать адрес входа (`null` — неизвестен, будет запрошен у сервера). */
export function setEntryZoneForTests(zone: EntryZone | null): void {
  resolved = zone
  cached = zone ? Promise.resolve(zone) : null
}

/** Название вкладки браузера кабинета сотрудников — чтобы не путать вкладки. */
export const TEAM_DOCUMENT_TITLE = 'Центрика — кабинет сотрудников'

/** Адрес входа; `null` — ещё не известен (подписи, зависящие от адреса, не показываются). */
export function useEntryZone(): EntryZone | null {
  const [zone, setZone] = useState<EntryZone | null>(resolved)
  useEffect(() => {
    // Ответ мог прийти между отрисовкой и эффектом (запрос уже идёт из EntryZoneTitle).
    if (resolved) {
      setZone(resolved)
      return
    }
    let alive = true
    void loadEntryZone().then((z) => {
      if (alive) setZone(z)
    })
    return () => {
      alive = false
    }
  }, [])
  return zone
}

/** На внутреннем адресе — название вкладки «Центрика — кабинет сотрудников». */
export function EntryZoneTitle() {
  const zone = useEntryZone()
  useEffect(() => {
    if (zone?.zone === 'team') document.title = TEAM_DOCUMENT_TITLE
  }, [zone])
  return null
}
